import HIGHLIGHT_MODES from "@modules/JourneyBuilder/Components/Charts/SankeyV2/highlight-modes";

/**
 * Center the element associated with the given node
 * @param {SVG} svg SVG element the node is located in
 * @param {string} nodeId nodeId to center
 */
function centerNode(svg, nodeId){
    const nodeElement = svg.findOne(`.node[data-nodeid="${nodeId}"]`);
    if(!nodeElement){
        return;
    }

    //getBBox returns the largest visible x/y from our inner nodes relative to the SVG element
    //our SVG element starts a bit above the middle of our page, thus scrolling to the given x/y should roughly center the element
    const {x, y} = nodeElement.node.getBBox();

    document.scrollingElement.scrollTo(x, y);
}

/**
 * Find all parent nodes for the given node
 *
 * @param {JourneyBuilder.Sankey.Tree} tree
 * @param {string} nodeId
 * @return {string[]}
 */
function getNodeParentIds(tree, nodeId){
    const parentIds = [];
    
    let branch = tree.getBranchByNodeId(nodeId);
    for(branch = branch.parent; branch; branch = branch.parent) {
        parentIds.push(branch.node.id);
    }
    
    return parentIds;
}

/**
 * Find all node and all link elements pointing towards the nodes with the given ids
 * @param {SVG} svg
 * @param {string[]} nodeIds
 * @return {{nodes: Dom[], links: Dom[]}}
 */
function findNodesAndLinks(svg, nodeIds) {
    const nodeSelector = nodeIds
        .map(nodeId => `[data-nodeid="${nodeId}"]`)
        .join(',');

    const linkSelector = nodeIds
        .map(nodeId => `.node-link[data-tonode="${nodeId}"]`)
        .join(',');

    return {
        //Empty selectors are not allowed
        links: (linkSelector && svg.find(linkSelector)) || [],
        nodes: (nodeSelector && svg.find(nodeSelector)) || []
    }
}

/**
 * https://datanext.atlassian.net/browse/DPN-2687?focusedCommentId=52566
 *
 * Find the ids of all highlighted nodes
 *
 * A node is highlighted when:
 *  It is the 'most taken' (= path without goal node)
 *  It is the 'least taken' (= path without goal node)
 *  It is the 'most successful' (= path with goal node)
 *  It is the 'least successful' (= path with goal node)
 *
 * The most/least taken paths are calculated by taking the sum of all nodes taken to arrive at the final node
 * thus a path consisting of 3 nodes with 500 records per node (= 1500 total) is considered less taken
 * than a path consisting of 8 nodes in total with 400 records per node (= 3200 total)
 *
 * Success of a goal node is calculated by dividing the goal's total by the total from the audience the
 * goal node originated from
 * thus a goal node with 5 records originating from an audience of 10 records (= 50%)
 * is considered more successful than a goal node 49 records originating from an audience of 100 records (= 49%)
 *
 * @param {JourneyBuilder.Sankey.Tree} tree
 * @param {HIGHLIGHT_MODES} highlightMode
 * @return {String[]} Highlighted node ids
 */
function findHighlightedNodeIds(tree, highlightMode){
    /** @type {JourneyBuilder.Sankey.TreeBranchV2[]} */
    const endBranches = [];
    
    tree.forAllBranches(branch => {
        if(!branch.children.length){
            endBranches.push(branch);
        }
    });
    
    const regularEndpointTotals = {};
    const goalEndpointSuccessRates = {};

    /**
     * @param {JourneyBuilder.Sankey.TreeBranchV2} currentBranch
     * @param {JourneyBuilder.Sankey.TreeBranchV2} endBranch
     */
    const processBranchPath = (currentBranch, endBranch) => {
        const currentNode = currentBranch.node;
        const endNode = endBranch.node;
        
        //Root node; this is to be used for determining goal success rate
        if(!currentNode.from){
            if(endNode.isGoal){
                goalEndpointSuccessRates[endNode.id] = endNode.total / currentNode.total;
            }

            return;
        }
        
        //non-goal endpoints use a total based system
        if(!endNode.isGoal){
            if(currentNode === endNode){
                regularEndpointTotals[endNode.id] = 0;
            }

            regularEndpointTotals[endNode.id] += currentNode.total;
        }

        processBranchPath(currentBranch.parent, endBranch);
    }

    //Start off by calculating totals/rates
    endBranches.forEach(endBranch => {
        //Goal nodes with a total of 0 are not to be used in 'success' calculations
        if(endBranch.node.isGoal && endBranch.node.total === 0){
            return;
        }

        processBranchPath(endBranch, endBranch);
    });

    /**
     * Find the min/max nodes in the given lookup based on node total/rate
     * @param { Object.<String, Number> } nodeValueLookup Node value lookup, can be rates or totals
     * @return {*[]|[Number, Number]}
     */
    const findMinMaxNodesByLookup = nodeValueLookup => {
        //Start off with 'impossible' values
        let minValue = Infinity
            , maxValue = -1
            , minValueNodeId = undefined
            , maxValueNodeId = undefined;

        Object.keys(nodeValueLookup).forEach(nodeId => {
            const nodeValue = nodeValueLookup[nodeId];

            if(nodeValue < minValue){
                minValueNodeId = nodeId;
                minValue = nodeValue;
            }

            if(nodeValue > maxValue){
                maxValueNodeId = nodeId;
                maxValue = nodeValue;
            }
        });

        //For now we just care about a node having the min/max value
        //we don't care about it being a goal/regular node, just it being highlighted
        //duplicates/undefined nodeIds should not be returned
        if(
            minValueNodeId === maxValueNodeId &&
            !maxValueNodeId
        ){
            return [];
        }

        return [minValueNodeId, maxValueNodeId];
    };

    function minValueNode(valueLookup){
        const [nodeId] = findMinMaxNodesByLookup(valueLookup);
        if(!nodeId){
            return [];
        }

        return [nodeId];
    }

    function maxValueNode(valueLookup){
        const [, nodeId] = findMinMaxNodesByLookup(valueLookup);
        if(!nodeId){
            return [];
        }

        return [nodeId];
    }

    switch(highlightMode){
        case HIGHLIGHT_MODES.LEAST_TAKEN_ROUTE:
            return minValueNode(regularEndpointTotals);
        case HIGHLIGHT_MODES.MOST_TAKEN_ROUTE:
            return maxValueNode(regularEndpointTotals);
        case HIGHLIGHT_MODES.LEAST_SUCCESSFUL_ROUTE:
            return minValueNode(goalEndpointSuccessRates);
        case HIGHLIGHT_MODES.MOST_SUCCESSFUL_ROUTE:
            return maxValueNode(goalEndpointSuccessRates);
    }
}


/**
 * Toggle highlighting for most/least successful routes/touchpoints
 * @param {SVG} svg
 * @param {JourneyBuilder.Sankey.Tree} tree
 * @param {HIGHLIGHT_MODES} highlightMode
 */
function renderNodeHighlights(svg, tree, highlightMode) {
    const isToggleOff = !highlightMode;

    //Start off with a 'clean' slate
    svg.find('.highlighted').forEach(element => element.removeClass('highlighted'));
    svg.find('.highlighted-link').forEach(element => element.removeClass('highlighted-link'));
    svg.find('.highlighted-parent').forEach(element => element.removeClass('highlighted-parent'));

    //Simply remove our highlight classes
    if(isToggleOff){
        return;
    }

    const highlightedNodeIds = findHighlightedNodeIds(tree, highlightMode);

    highlightedNodeIds.forEach(rootNodeId => {
        const rootNode = svg.findOne(`[data-nodeid="${rootNodeId}"]`);

        rootNode.addClass('highlighted');

        const { nodes: highlightedNodes, links: highlightedLinks } = findNodesAndLinks(
            svg,
            [rootNodeId, ...getNodeParentIds(tree, rootNodeId)]
        );

        highlightedNodes
            .forEach(node => {
                //Prevent root from also being marked as parent
                if(node.data('nodeid') !== rootNodeId){
                    node.addClass('highlighted-parent')
                }
            });

        highlightedLinks.forEach(link => link.addClass('highlighted-link'));
    });
}

/**
 * Render the state of the currently selected node
 * @param {SVG} svg
 * @param {JourneyBuilder.Sankey.Tree} tree
 * @param {String} selectedBrickId
 */
function renderSelectedNode(svg, tree, selectedBrickId){
    const selectedNodeIds = [];
    tree.forAllBranches(branch => {
        if(branch.node.brickId === selectedBrickId) {
            selectedNodeIds.push(branch.node.id);
        }
    });
    
    // const selectedBranch = tree.getBranchByNodeId(selectedNodeId);

    //Ignore invalid nodeIds, may happen upon getting assigned a new dataset
    if(selectedBrickId && !selectedNodeIds.length) {
        return;
    }

    //Always start off by rendering all nodes deselected again
    svg.find('.active')
        .forEach(link => link.removeClass('active'));

    svg.find('.selected')
        .forEach(node => node.removeClass('selected'));

    //No nodeId implies deselection - we're done
    if(!selectedBrickId){
        return;
    }

    const selectedNodesSelector = selectedNodeIds
        .map(nodeId => `[data-nodeid="${nodeId}"]`)
        .join(',');
    
    const selectedNodeElement = svg.find(selectedNodesSelector);

    //An element needs to be present in the DOM for us to be able highlight it
    if(!selectedNodeElement.length){
        return;
    }

    //Upon selection; only the current node/direct upstream relations should remain marked active
    const activeNodeIds = selectedNodeIds.flatMap(nodeId => [
        nodeId,
        ...getNodeParentIds(tree, nodeId)
    ]);

    //TODO: Convert to :not(...) range when ranges are widely supported
    //https://developer.mozilla.org/en-US/docs/Web/CSS/:not#Syntax

    const nodeAndLinkSelector = activeNodeIds
        .map(nodeId => `.node-link[data-tonode="${nodeId}"],.node[data-nodeid="${nodeId}"]`)
        .join(',');

    selectedNodeElement.forEach(node => node.addClass('selected'));
    svg.find(nodeAndLinkSelector).forEach(link => link.addClass('active'));
}

export {
    renderSelectedNode,
    renderNodeHighlights,
    centerNode
}